"use strict";

customElements.define(
  "swipeable-control",
  class extends HTMLElement {
    constructor() {
      super();

      this.hasSwiped = false;
    }

    get findRelatedSlider() {
      if (this.hasAttribute("related")) {
        return document.querySelector("#" + this.getAttribute("related"));
      }

      if (this.closest("swipeable-area")) {
        return this.closest("swipeable-area");
      }
    }

    set relatedSlider(node) {
      this.slider = node;
    }

    useSlider() {
      if (!this.slider) {
        this.relatedSlider = this.findRelatedSlider;

        if (!this.slider) return;
      }

      this.slider.moveSlider(this.hasAttribute("alternate"));
    }

    connectedCallback() {
      this.relatedSlider = this.findRelatedSlider;
      this.addEventListener("click", this.useSlider.bind(this));
    }
  }
);


customElements.define(
  "swipeable-area",
  class extends HTMLElement {
    constructor() {
      super();
      this.cordinateIndex = 0;

      this.hasHostedControls = false;
      this.eventsBound = false;

      this.relevantClasses = {
        item: "swipeable-item",
        slider: "swipeable-slider",
      };
    }

    get allOptions() {
      const availableOptions = {
        m: {
          direction: "x",
          viewport: {
            max: 767,
          },
        },
        mm: {
          direction: "x",
          threshold: 35,
          viewport: {
            max: 767,
          },
        },
        t: {
          direction: "x",
          viewport: {
            min: 768,
            max: 979,
          },
        },
        d: {
          direction: "x",
          viewport: {
            min: 980,
          },
        },
        gx: {
          direction: "x",
          threshold: 50,
          viewport: {
            min: 980,
          },
        },
        g: {
          direction: "y",
          viewport: {
            min: 980,
          },
        },
      };

      let selectedOptions = [];
      let attributeOptions = this.getAttribute("options");
      let options = attributeOptions ? attributeOptions.split(",") : [];

      options.forEach((option) => {
        let splitOption = option.split("-");
        let name = splitOption[0].toLowerCase();
        let matchingOption = availableOptions[name];

        if (matchingOption) {
          let direction = "x";
          if (splitOption.length > 1) direction = splitOption[1];

          if (direction !== "x") {
            matchingOption.direction = direction;
          }

          selectedOptions.push(matchingOption);
        }
      });

      return selectedOptions;
    }

    get applicableOption() {
      const viewport = window.innerWidth;
      let applicableOption = false;

      this.allOptions.forEach((option) => {
        const min = option.viewport.min;
        const max = option.viewport.max;
        let applicable;

        if (min && max) applicable = viewport >= min && max >= viewport;
        if (min && !max) applicable = viewport >= min;
        if (max && !min) applicable = max >= viewport;
        if (!applicable) return false;

        applicableOption = option;
      });

      return applicableOption;
    }

    get allowedToRun() {
      if (!this.option) return false;
      if (!this?.rect?.slider) return false;
      const allowedDirection = this.option.direction || "x";

      let allowedToRun = false;
      if (allowedDirection === "x")
        allowedToRun = this.rect.slider.width >= this.rect.width;
      if (allowedDirection === "y")
        allowedToRun = this.rect.slider.height >= this.rect.height;

      return allowedToRun;
    }

    get getSiblings() {
      var DOMSiblings = false;
      if (this.group) {
        DOMSiblings = document.querySelectorAll(
          'swipeable-area[group="' + this.group + '"]'
        );
      }

      return DOMSiblings;
    }

    set setState(attr) {
      this.state = attr;
      this.setAttribute("state", attr);
    }

    build() {
      this.slider = this.querySelector("." + this.relevantClasses.slider);
      if (!this.slider) return;
      this.items = this.slider.querySelectorAll(
        "." + this.relevantClasses.item
      );
      if (!this.items || this.items.length === 0) return;

      const rect = this.getBoundingClientRect(),
        firstItemRect = this.items[0].getBoundingClientRect(),
        lastItemRect =
          this.items[this.items.length - 1].getBoundingClientRect();

      const itemWidths = Array.from(
          this.items,
          (item) => item.getBoundingClientRect().width
        ),
        itemHeights = Array.from(
          this.items,
          (item) => item.getBoundingClientRect().height + window.scrollY
        ),
        averageItemWidth =
          itemWidths.reduce((prev, curr) => prev + curr) / itemWidths.length,
        averageItemHeight =
          itemWidths.reduce((prev, curr) => prev + curr) / itemHeights.length,
        sliderWidth = lastItemRect.x - firstItemRect.x + lastItemRect.width,
        sliderHeight = lastItemRect.y - firstItemRect.y + lastItemRect.height;

      let direction = (this.option && this.option.direction) || "x";
      let itemCordinates = Array.from(
        this.items,
        (item) => item.getBoundingClientRect()[direction]
      );
      let totalGap =
        sliderWidth - itemWidths.reduce((prev, curr) => prev + curr);

      if (this.option && this.option.direction === "y") {
        totalGap =
          sliderHeight - itemHeights.reduce((prev, curr) => prev + curr);
      }

      const gapPerItem = Math.ceil(totalGap / (this.items.length - 1)),
        averageWidthWithGap = averageItemWidth + gapPerItem,
        averageHeightWithGap = averageItemHeight + gapPerItem,
        parsedMoveAttribute = parseInt(this.getAttribute("move"));

      // Used to help with moving elements
      this.rect = {
        width: rect.width,
        height: rect.height,
        startX: firstItemRect.x,
        startY: firstItemRect.y + window.scrollY,
        slider: {
          width: sliderWidth,
          height: sliderHeight,
          gap: totalGap,
        },
        items: {
          widths: itemWidths,
          heights: itemHeights,
          averageWidth: averageItemWidth,
          averageWidthWithGap: averageWidthWithGap,
          averageHeight: averageItemHeight,
          averageHeightWithGap: averageHeightWithGap,
          gapPerItem: gapPerItem,
        },
      };

      // Information about how things are handled
      this.settings = {
        useEndEarly: this.hasAttribute("end-early"),
        offsetItems: this.hasAttribute("offset-items"),
        move: isNaN(parsedMoveAttribute) ? 1 : parsedMoveAttribute,
        lastItemIndex: this.items.length - 1,
        countAsASwipeThreshold: 5,
        direction: (this.option && this.option.direction) || "x",
        isSibling: this.hasAttribute("sibling"),
      };

      this.settings.itemsPerView = Math.floor(rect.width / averageWidthWithGap);
      this.settings.swipingThreshold = this.rect.items.averageWidth / 2;

      if (this.option) {
        if (this.option.direction === "y") {
          this.settings.itemsPerView = Math.floor(
            rect.height / averageHeightWithGap
          );
          this.settings.swipingThreshold = this.rect.items.averageHeight / 2;
        }

        if (this.option.threshold) {
          this.settings.swipingThreshold = this.option.threshold;
        }
      }

      if (this.settings.useEndEarly) {
        this.settings.lastItemIndex =
          this.items.length - this.settings.itemsPerView;
      }

      this.coordinates = itemCordinates.map(
        (cordinate) => cordinate - itemCordinates[0]
      );

      // Group name is used to connect with other of the same customElement type
      this.group = this.hasAttribute("group")
        ? this.getAttribute("group")
        : false;
    }

    getCorrectIndex(movement, specificIndex) {
      let calculatedIndex = this.cordinateIndex + movement;
      let lastItemIndex = this.settings.lastItemIndex;
      calculatedIndex = specificIndex >= 0 ? specificIndex : calculatedIndex;

      if (0 >= calculatedIndex) {
        this.setState = "start";
        return 0;
      }

      if (this.settings.offsetItems) {
        if (calculatedIndex > 0) {
          lastItemIndex += 1;
        }
      }

      if (calculatedIndex >= lastItemIndex) {
        this.setState = "end";
        return lastItemIndex;
      }

      this.setState = "running";
      return calculatedIndex;
    }

    /**
     * @param {number} amount
     */
    transformSlider(amount, concreteIndex) {
      let transformAmount = amount;
      let axis = this.settings.direction.toUpperCase();

      if (concreteIndex) {
        transformAmount = this.coordinates[concreteIndex];
      }

      this.slider.style.transform =
        "translate" + axis + "(-" + transformAmount + "px)";
    }

    /**
     * @param {boolean} alternate
     * @param {integer} specificIndex
     */
    moveSlider(alternate, specificIndex, siblingCall) {
      let initialIndex = this.settings.move * (alternate ? -1 : 1);

      let index = this.getCorrectIndex(initialIndex, specificIndex);

      this.cordinateIndex = index;
      let transformIndex = this.cordinateIndex;

      if (this.settings.offsetItems) {
        if (this.cordinateIndex > 0) {
          transformIndex -= 1;
        }
      }

      this.transformSlider(this.coordinates[transformIndex]);

      let activeItemIndex = index;

      if (index !== specificIndex) {
        activeItemIndex = specificIndex;
      }

      this.markActiveItem(activeItemIndex);

      if (!siblingCall) {
        this.changeSiblingIndex(initialIndex, specificIndex);
      }
    }

    thumbnailCallback(thumbnailIndex) {
      if (!this.hasSwiped) {
        this.moveSlider(null, thumbnailIndex);
      }
    }

    hostControls() {
      if (!this.hasAttribute("host-controls")) return;
      if (this.hasHostedControls) return;
      if (this.state === "disabled") return;

      const controlTemplate = document.querySelector(
        ".template-swipeable-controls"
      );

      if (!controlTemplate) return;

      // Spawn controllers
      let controls =
        controlTemplate.content.querySelectorAll("swipeable-control");

      controls.forEach((control) => {
        let clone = control.cloneNode(true);

        // "Bind controller to the slider" via related attribute
        if (!clone.hasAttribute("related")) {
          clone.setAttribute("related", this.id);
        }

        this.appendChild(clone);
      });

      this.hasHostedControls = true;
    }

    initSwiping() {
      if (this.state === "disabled") return;

      const prevCordinates = {
        x: 0,
        y: 0,
      };

      let selectedAxis = this.option.direction,
        isSwiping = false,
        startTransform = 0,
        prevTransform = 0,
        currentTransform = 0,
        swipingForward = true;

      const resetVariables = () => {
        let index = this.cordinateIndex;

        if (this.settings.offsetItems) {
          if (index > 0) {
            index -= 1;
          }
        }

        startTransform = this.coordinates[index];
        currentTransform = this.coordinates[index];
        prevTransform = this.coordinates[index];
      };

      // Get ready to swipe
      const startSwiping = (e) => {
        let targetIsController =
          e.target.nodeName === "swipeable-control" ||
          e.target.closest("swipeable-control");

        // Check if the target is a controller (or child of a controller)
        // Allow swipe if the controller is used to select a specific item index (Like for gallery thumbnails)
        if (
          !!targetIsController &&
          !targetIsController.hasAttribute("specific-index")
        )
          return;

        isSwiping = true;

        prevCordinates.x = (e.touches && e.touches[0].clientX) || e.clientX;
        prevCordinates.y = (e.touches && e.touches[0].clientY) || e.clientY;

        resetVariables();
        this.setAttribute("swipping", "");
      };

      // Make ready for next swipe
      const stopSwiping = (e) => {
        let itemIndex = this.cordinateIndex;
        let transformDifference = Math.abs(currentTransform - startTransform);

        if (transformDifference > this.settings.swipingThreshold) {
          // 1 since we are past the threshold
          let moveAmount = 1;

          // Remove the threshold amount from the difference
          let differenceAfterThreshold =
            transformDifference - this.settings.swipingThreshold;

          // How many items (including gap), have we moved extra?
          let fullItemsMoved = Math.floor(
            differenceAfterThreshold / this.rect.items.averageWidthWithGap
          );

          // Check how many items with gap, we have moved past the threshold
          moveAmount = moveAmount + fullItemsMoved;

          itemIndex = this.getCorrectIndex(
            moveAmount * (swipingForward ? -1 : 1)
          );
          this.markActiveItem(itemIndex);
        }

        // Can be used when exiting a swiping circle
        if (
          !this.hasSwiped &&
          transformDifference > this.settings.countAsASwipeThreshold
        )
          this.hasSwiped = true;

        let transformIndex = itemIndex;

        if (this.settings.offsetItems) {
          if (itemIndex > 0) {
            transformIndex -= 1;
          }
        }

        this.transformSlider(this.coordinates[transformIndex]);

        // Don't change siblings, if the index hasn't changed
        if (itemIndex !== this.cordinateIndex) {
          this.changeSiblingIndex(itemIndex);
        }

        // Setup for next swipe
        this.cordinateIndex = itemIndex;
        resetVariables();
        isSwiping = false;
        this.removeAttribute("swipping");
      };

      // Start swiping
      const swiping = (e) => {
        if (!isSwiping) return;
        let difference = 0;

        const currentCordinates = {
          x: (e.touches && e.touches[0].clientX) || e.clientX,
          y: (e.touches && e.touches[0].clientY) || e.clientY,
        };

        if (prevCordinates[selectedAxis] > currentCordinates[selectedAxis])
          swipingForward = false;
        if (currentCordinates[selectedAxis] > prevCordinates[selectedAxis])
          swipingForward = true;

        difference =
          currentCordinates[selectedAxis] - prevCordinates[selectedAxis];
        currentTransform = prevTransform - difference;

        this.transformSlider(currentTransform);
        prevTransform = currentTransform;

        prevCordinates.x = currentCordinates.x;
        prevCordinates.y = currentCordinates.y;
      };

      if (!this.eventsBound) {
        this.addEventListener("mousedown", startSwiping);
        this.addEventListener("touchstart", startSwiping, { passive: true });

        this.addEventListener("mousemove", swiping.bind(this));
        this.addEventListener("touchmove", swiping.bind(this), {
          passive: true,
        });

        this.addEventListener("mouseup", stopSwiping);
        this.addEventListener("mouseleave", stopSwiping);
        this.addEventListener("touchend", stopSwiping, { passive: true });

        this.addEventListener("click", (e) => {
          let preventEvent = false;

          if (this.hasSwiped) {
            if (e.target.nodeName === "a" || e.target.closest("a")) {
              preventEvent = true;
            }

            if (e.target.nodeName === "img" || e.target.closest("img")) {
              preventEvent = true;
            }
          }

          if (preventEvent) e.preventDefault();

          if (this.hasSwiped) this.hasSwiped = false;
        });

        this.eventsBound = true;
      }
    }

    changeSiblingIndex(givenIndex, specificIndex) {
      let itemIndex = specificIndex >= 0 ? specificIndex : givenIndex;

      if (!this.siblings) {
        this.siblings = this.getSiblings;
      }

      // Check again
      if (!this.siblings) return;

      this.siblings.forEach((sibling) => {
        if (sibling.id !== this.id) {
          sibling.moveSlider(null, itemIndex, true);
        }
      });
    }

    get activeItem() {
      return Array.from(this.items).filter((item) => {
        return item.classList.contains("swipeable-item-active");
      })[0];
    }

    markActiveItem(itemIndex = this.cordinateIndex) {
      if (!this.items || this.items.length === 0) return;

      if (this.activeItem) {
        this.pauseItemVideo(this.activeItem); // Pause video, before changing to another item
        this.activeItem.classList.remove("swipeable-item-active");
      }

      this.items[itemIndex].classList.add("swipeable-item-active");
      this.playItemVideo(this.activeItem);
    }

    pauseItemVideo(item) {
      let videoElement = item.querySelector(".productpage-media__video");
      if (!videoElement) return;

      if (videoElement.nodeName === "IFRAME") {
        videoElement.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*"
        );
      }

      if (videoElement.nodeName === "VIDEO") {
        videoElement.pause();
      }
    }

    playItemVideo(item) {
      let videoElement = item.querySelector(".productpage-media__video");
      if (!videoElement) return;

      if (videoElement.nodeName === "IFRAME") {
        videoElement.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          "*"
        );
      }

      if (videoElement.nodeName === "VIDEO") {
        videoElement.play();
      }
    }

    init() {
      this.option = this.applicableOption;
      this.build();
      this.setState = this.allowedToRun ? "start" : "disabled";
      this.hostControls();
      this.initSwiping();

      // Avoid flicker on load - if active item
      if (!this.activeItem) {
        this.markActiveItem();
      }
    }

    connectedCallback() {
      this.options = this.allOptions;
      this.init();
      window.addEventListener("resize", this.init.bind(this));
      document.body.addEventListener(
        "reload:swipeable-area",
        this.init.bind(this)
      );
    }
  }
);

customElements.define(
  "swipeable-thumbnail",
  class extends HTMLElement {
    constructor() {
      super();

      this.swiper = this.closest("swipeable-area");
      this.index = !isNaN(parseInt(this.getAttribute("specific-index")))
        ? parseInt(this.getAttribute("specific-index"))
        : 0;
    }

    moveSlider() {
      this.swiper.thumbnailCallback(this.index);
    }

    connectedCallback() {
      this.addEventListener("click", () => {
        this.moveSlider();
      });
    }
  }
);
